import { useEffect, useId, useRef, useState } from 'react'
import { entryInCompetition, type GetEntryOutput, type TEntrySummary, type TShow } from '@schemas'
import { toast } from 'sonner'
import { useDivisions, useMarkLabelPrinted, useReadEntry } from '@/api/hooks'
import { useActorPermissions } from '@/hooks/useActorPermissions'
import { useLabelPrinter } from '@/context/label-printer'
import { showAllowsLabelPrinting } from '@/lib/label/permissions'
import { resolveLabelsPerExhibitFromClass } from '@/lib/labels-per-exhibit'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'

// Class label counts are single digits. The ceiling keeps a mistyped Exhibit ID or class setting
// out of the print queue.
const MAX_COPIES = 20
const SAVE_FAILED_MESSAGE =
  'Labels were sent to the printer, but their printed status could not be saved. Check the output before printing again.'

type ReadEntry = GetEntryOutput['entry']
type EntryRead = { state: 'reading' } | { state: 'failed' } | { state: 'current'; entry: ReadEntry }

// What a label depends on. A print goes ahead only if this still matches what the dialog showed.
const printBasis = (entry: ReadEntry) =>
  JSON.stringify([
    entry.show.id,
    entry.entryCode,
    entry.status,
    entry.class?.id ?? null,
    entry.class?.code ?? null,
    entry.exhibit?.id ?? null,
    entry.show.status ?? null,
    Boolean(entry.show.labelPrintingDisabled),
  ])

export type LabelPrintShow = Pick<TShow, 'id' | 'name' | 'status' | 'labelPrintingDisabled'>
type Props = { entry: TEntrySummary; show: LabelPrintShow }

export const toLabelPrintEntry = (entry: ReadEntry): TEntrySummary => ({
  id: entry.id!,
  entryCode: entry.entryCode,
  exhibitId: entry.exhibit?.id,
  status: entry.status,
  class: entry.class,
})

export function EntryLabelPrintControl({ entry, show }: Props) {
  const printer = useLabelPrinter()
  const permissions = useActorPermissions(show.id)
  const divisions = useDivisions(show.id)
  const markPrinted = useMarkLabelPrinted()
  const readEntry = useReadEntry()
  const inputId = useId()
  const [online, setOnline] = useState(() => navigator.onLine)
  const [open, setOpen] = useState(false)
  const openRef = useRef(false)
  const [read, setRead] = useState<EntryRead>({ state: 'reading' })
  const readSeq = useRef(0)
  const [copiesText, setCopiesText] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const inFlight = useRef(false)
  const [message, setMessage] = useState<string | null>(null)
  const [printed, setPrinted] = useState(false)
  // The exhibit whose labels are out but whose printed status is not yet saved.
  const unsavedRef = useRef<string | null>(null)

  useEffect(() => {
    const update = () => setOnline(navigator.onLine)
    window.addEventListener('online', update)
    window.addEventListener('offline', update)
    return () => {
      window.removeEventListener('online', update)
      window.removeEventListener('offline', update)
      // A job that outlives this row reports by toast, like one that outlives the dialog.
      openRef.current = false
    }
  }, [])

  // Callers hand over copies of varying age (a live list, a page snapshot, a workstation record).
  // The open dialog works from its own read; the caller's copy only decides whether it can open.
  const readNow = open && read.state === 'current' ? read.entry : null
  const current = readNow ? toLabelPrintEntry(readNow) : entry
  const klass = divisions.data
    ?.flatMap(division => division.classes ?? [])
    .find(item => item.id === current.class?.id)
  // Entry managers who cannot read classes, and failed polls with nothing cached, still print.
  const classSettingsUnavailable = !divisions.data && divisions.isError
  const classCopies = resolveLabelsPerExhibitFromClass(klass)
  const defaultCopies = Math.min(classCopies, MAX_COPIES)
  const pending = busy || printer.getJobStatus(entry.id)?.status === 'pending'
  const saveToastId = `label-status-${entry.id}`

  const getDisabledReason = () => {
    // A caller can still hold an entry from a show that is no longer the selected one.
    if (readNow && readNow.show.id !== show.id)
      return 'This entry belongs to a different show than the one selected.'
    if (show.labelPrintingDisabled || readNow?.show.labelPrintingDisabled)
      return 'Label printing is disabled for this show.'
    if (!showAllowsLabelPrinting(show) || (readNow && !showAllowsLabelPrinting(readNow.show)))
      return 'Label printing is available when the show is accepting entries.'
    if (!permissions.can('entries:write'))
      return 'Printing labels requires permission to manage entries.'
    if (!entryInCompetition(current.status))
      return `${current.status === 'disqualified' ? 'Disqualified' : 'Not Entered'} entries cannot have labels printed.`
    if (!current.exhibitId) return 'Check in this entry before printing labels.'
    if (!/^[1-9]\d{3}$/.test(current.entryCode))
      return 'Printing requires a canonical four-digit Exhibit ID.'
    if (!online) return 'Connect to the network to print labels.'
    if (printer.strategy === 'dymo' && !printer.selectedPrinter)
      return 'Select a DYMO printer before printing.'
    if (divisions.isLoading) return 'Loading class label settings…'
    return undefined
  }
  const disabledReason = getDisabledReason()

  // Until the count is edited it follows the class of the entry as read.
  const copiesValue = copiesText ?? String(defaultCopies)
  const copies = Number(copiesValue)
  const validCopies = /^\d+$/.test(copiesValue) && copies >= 1 && copies <= MAX_COPIES
  const canPrint = !disabledReason && !pending && Boolean(readNow) && validCopies

  const run = async (task: () => Promise<void>) => {
    if (inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setMessage(null)
    try {
      await task()
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  const offerStatusSave = (exhibitId: string) =>
    toast.error(SAVE_FAILED_MESSAGE, {
      id: saveToastId,
      duration: Infinity,
      action: {
        label: 'Save status',
        onClick: event => {
          // Keep the offer on screen while another job for this entry is running.
          if (inFlight.current) event.preventDefault()
          else void run(() => savePrinted(exhibitId))
        },
      },
    })

  const changeOpen = (next: boolean) => {
    openRef.current = next
    setOpen(next)
    // Closing must not discard the only way to save the status without printing again.
    if (!next && unsavedRef.current) offerStatusSave(unsavedRef.current)
  }

  // A job outlives a closed dialog, so its outcome then goes to a toast.
  const savePrinted = async (exhibitId: string) => {
    try {
      await markPrinted.mutateAsync({ exhibitId })
    } catch {
      unsavedRef.current = exhibitId
      setMessage(SAVE_FAILED_MESSAGE)
      if (!openRef.current) offerStatusSave(exhibitId)
      return
    }
    unsavedRef.current = null
    toast.dismiss(saveToastId)
    toast.success('Labels sent to printer')
    changeOpen(false)
  }

  const report = (text: string) => {
    setMessage(text)
    if (!openRef.current) toast.error(text)
  }

  const print = (shown: ReadEntry, exhibitId: string) =>
    run(async () => {
      // The dialog may have been open a while. Print only if the entry still matches what it shows.
      let fresh: ReadEntry
      try {
        fresh = await readEntry.mutateAsync(entry.id)
      } catch {
        setRead({ state: 'failed' })
        if (!openRef.current) toast.error('The entry could not be read, so no labels were printed.')
        return
      }
      if (printBasis(fresh) !== printBasis(shown)) {
        setRead({ state: 'current', entry: fresh })
        setCopiesText(null)
        report('This entry changed while the dialog was open. Check the details, then print again.')
        return
      }
      try {
        await printer.printEntryLabel(
          {
            entryCode: current.entryCode,
            classCode: current.class?.code || klass?.code || null,
            showName: show.name,
          },
          { copies, jobKey: entry.id }
        )
      } catch (error) {
        report(error instanceof Error ? error.message : 'Unable to print labels. Try again.')
        return
      }
      setPrinted(true)
      await savePrinted(exhibitId)
    })

  const submit = () => {
    if (pending) return
    // Once the labels are out, the only step left to repeat is saving their status.
    const unsaved = unsavedRef.current
    if (printed) {
      if (unsaved) void run(() => savePrinted(unsaved))
    } else if (canPrint && readNow && current.exhibitId) void print(readNow, current.exhibitId)
  }

  const start = () => {
    setCopiesText(null)
    setMessage(null)
    setPrinted(false)
    unsavedRef.current = null
    const seq = ++readSeq.current
    setRead({ state: 'reading' })
    // Only the latest opening's read may settle the state.
    readEntry.mutateAsync(entry.id).then(
      fresh => {
        if (seq === readSeq.current) setRead({ state: 'current', entry: fresh })
      },
      () => {
        if (seq === readSeq.current) setRead({ state: 'failed' })
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          // Button drops pointer events when disabled, which would hide the reason tooltip.
          className="disabled:pointer-events-auto"
          disabled={Boolean(disabledReason) || pending}
          title={disabledReason}
          aria-label={`Print labels for Exhibit ID ${entry.entryCode}`}
          onClick={start}
        >
          {pending ? 'Printing…' : 'Print labels'}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Print labels</DialogTitle>
          <DialogDescription>
            Exhibit ID {current.entryCode} · {current.class?.name || 'Entry'}. Choose the number of
            individual labels to print.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={event => {
            event.preventDefault()
            submit()
          }}
          className="space-y-4 pt-4"
        >
          <div className="space-y-2">
            <label htmlFor={inputId} className="text-sm font-medium">
              Copies
            </label>
            <Input
              id={inputId}
              type="number"
              min="1"
              max={MAX_COPIES}
              step="1"
              value={copiesValue}
              disabled={pending || printed}
              onChange={event => setCopiesText(event.target.value)}
            />
            {!validCopies && (
              <p className="text-sm text-destructive">
                Enter a whole number from 1 to {MAX_COPIES}.
              </p>
            )}
            {classCopies > MAX_COPIES && (
              <p role="status" className="text-sm">
                This class is set to {classCopies} labels. One print run is limited to {MAX_COPIES}.
              </p>
            )}
            {classSettingsUnavailable && (
              <p role="status" className="text-sm">
                Class label settings could not be loaded, so Copies starts at 1.
              </p>
            )}
          </div>
          {disabledReason && (
            <p role="status" className="text-sm">
              {disabledReason}
            </p>
          )}
          {read.state === 'reading' && (
            <p role="status" className="text-sm">
              Checking this entry before printing…
            </p>
          )}
          {read.state === 'failed' && !printed && (
            <p role="alert" className="text-sm">
              This entry could not be read, so nothing can be printed from it yet. Close this dialog
              and choose Print labels again to retry.
            </p>
          )}
          {pending && (
            <p role="status" className="text-sm">
              You can close this dialog. The result will appear as a notification.
            </p>
          )}
          {message && (
            <p role="alert" className="text-sm">
              {message}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => changeOpen(false)}>
              {pending || printed ? 'Close' : 'Cancel'}
            </Button>
            {printed ? (
              <Button type="submit" disabled={pending}>
                {pending ? 'Saving…' : 'Save printed status'}
              </Button>
            ) : (
              <Button type="submit" disabled={!canPrint}>
                {pending ? 'Printing…' : 'Print'}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
